interface MarkdownSection {
  title: string;
  content: string;
}

export function parseMarkdown(
  markdown: string,
): { title: string; sections: MarkdownSection[] } {
  // Get the main title (first h1)
  const titleMatch = markdown.match(/^# (.*?)$/m);
  const title = titleMatch ? titleMatch[1] : 'Document';

  // Capture the introduction text (between h1 and first h2)
  let introText = '';
  if (titleMatch) {
    const titleIndex = markdown.indexOf(titleMatch[0]);
    const titleEndIndex = titleIndex + titleMatch[0].length;
    const firstH2Index = markdown.indexOf('## ', titleEndIndex);

    if (firstH2Index > titleEndIndex) {
      introText = markdown.substring(titleEndIndex, firstH2Index).trim();
    }
  }

  // Split the document by h2 sections (## headings)
  const sections: MarkdownSection[] = [];

  // Process introduction if it exists
  if (introText) {
    sections.push({
      title: '',
      content: processContent(introText),
    });
  }

  // Use regex to match all sections starting with ##
  const sectionRegex = /## (.*?)(?=\n## |$)/gs;
  let match;

  while ((match = sectionRegex.exec(markdown)) !== null) {
    const fullSection = match[0];
    const sectionTitleMatch = fullSection.match(/## (.*?)$/m);

    if (sectionTitleMatch) {
      const sectionTitle = sectionTitleMatch[1];
      // Remove the section title from the content and clean up
      const sectionContent = fullSection.replace(/## .*?$/m, '').trim();

      sections.push({
        title: sectionTitle,
        content: processContent(sectionContent),
      });
    }
  }

  return { title, sections };
}

// Helper function to process markdown content
function processContent(content: string): string {
  // Process markdown for basic formatting
  // Replace **text** with <strong>text</strong>
  content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Replace *text* with <em>text</em>
  content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // Replace [text](url) with <a href="url">text</a>
  content = content.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" class="text-amber-400 hover:text-amber-300 underline">$1</a>',
  );

  // Replace ### headings with <h3> tags
  content = content.replace(
    /### (.*?)$/gm,
    '<h3 class="text-lg font-medium text-zinc-100 mt-6 mb-2">$1</h3>',
  );

  // Process lists first to prevent extra line breaks in list items

  // Replace - list items with <li> tags
  content = content.replace(/^- (.*?)$/gm, '<li>$1</li>');

  // Replace numeric lists (1. 2. 3.) with <li> tags
  content = content.replace(/^\d+\. (.*?)$/gm, '<li>$1</li>');

  // Wrap consecutive <li> tags with <ul> or <ol>
  content = content.replace(/(<li>.*?<\/li>\n)+/g, (match) => {
    // Check if this is a numbered list
    if (match.match(/^\d+\./m)) {
      return `<ol class="list-decimal pl-6 mb-4 space-y-1 text-zinc-300">${match}</ol>`;
    }
    return `<ul class="list-disc pl-6 mb-4 space-y-1 text-zinc-300">${match}</ul>`;
  });

  // Replace --- with divider
  content = content.replace(/---/g, '<hr class="my-6 border-zinc-700" />');

  // Handle paragraphs with proper spacing
  // Split into paragraphs (double newlines)
  const paragraphs = content.split(/\n\n+/);

  // Process each paragraph, but exclude those that are already HTML elements
  content = paragraphs.map((p) => {
    p = p.trim();
    if (!p) return '';

    // Skip already processed HTML elements (lists, headings, etc.)
    if (p.startsWith('<') && p.endsWith('>')) {
      return p;
    }

    // Replace single newlines with spaces within paragraphs
    p = p.replace(/\n/g, ' ');

    // Wrap non-HTML content in paragraph tags
    if (!p.startsWith('<') || !p.endsWith('>')) {
      return `<p class="mb-4 text-zinc-300">${p}</p>`;
    }
    return p;
  }).join('\n');

  return content;
}
